import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, Coffee, Linkedin } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer, { LINKEDIN_URL } from "../components/Footer";
import PostCard from "../components/PostCard";
import LeadCaptureForm from "../components/LeadCaptureForm";
import { captureSource, getSignupSource, getRefCode } from "../lib/source";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEADSHOT = "/images/elizabeth-portrait.jpg";

export default function Landing() {
  const [posts, setPosts] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    captureSource();
    axios.get(`${API}/posts?limit=5`).then((r) => setPosts(r.data.posts || [])).catch(() => {});
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/subscribe`, { email, name, source: getSignupSource(), ref: getRefCode() });
      localStorage.setItem("fb_sub_email", email.toLowerCase());
      setSubscribed(true);
      toast.success("You're in! Check your inbox soon.");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* ─── Hero ────────────────────────────────────────────────────── */}
      <section className="pt-28 pb-20 px-4 sm:px-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Bio */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <p className="overline mb-4">Newsletter by Elizabeth Neal</p>
            <h1 className="font-cormorant text-5xl sm:text-6xl font-semibold leading-tight tracking-tight text-foreground mb-6">
              Words that work for{" "}
              <span className="italic text-brand-accent">your business</span>
            </h1>
            <p className="font-manrope text-base text-muted-foreground leading-relaxed mb-8">
              I help businesses, entrepreneurs, and organizations attract more customers through
              clear, engaging content — blog posts, articles, newsletters, and white papers that
              build authority and strengthen customer relationships.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/subscribe"
                data-testid="hero-subscribe-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors duration-200"
              >
                <Mail size={16} />
                Subscribe — it's free
              </Link>
              <Link
                to="/hire"
                data-testid="hero-hire-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-border font-manrope text-sm font-medium text-foreground hover:bg-muted transition-colors duration-200"
              >
                Hire Elizabeth
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>

          {/* Right: Headshot */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden max-w-sm mx-auto lg:mx-0 lg:ml-auto shadow-lg bg-white">
              <img
                src={HEADSHOT}
                alt="Elizabeth Neal"
                className="w-full h-auto"
              />
              {/* Decorative accent */}
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 bg-card border border-border rounded-xl px-4 py-3 shadow-md">
              <p className="overline text-brand-accent mb-1">Writing since 2017</p>
              <p className="font-cormorant text-xl font-semibold text-foreground">Elizabeth Neal</p>
              <p className="font-manrope text-xs text-muted-foreground">Freelance Writer & Blogger</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Newsletter Signup ────────────────────────────────────────── */}
      <section className="py-14 px-4 sm:px-6 bg-card border-y border-border">
        <div className="max-w-2xl mx-auto text-center">
          <p className="overline mb-3">Join the community</p>
          <h2 className="font-cormorant text-3xl sm:text-4xl font-semibold text-foreground mb-3">
            Get writing insights, free
          </h2>
          <p className="font-manrope text-sm text-muted-foreground mb-8">
            No spam, ever. Just practical writing tips and content strategy delivered to your inbox.
          </p>
          {subscribed ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-3"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 font-manrope text-sm font-medium">
                You're subscribed! Welcome aboard.
              </div>
              <Link
                to="/referrals"
                data-testid="signup-referral-cta"
                className="font-manrope text-sm text-brand-accent hover:underline"
              >
                Refer friends & earn free premium →
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="signup-name-input"
                className="flex-1 px-4 py-2.5 rounded-full border border-border bg-background font-manrope text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              />
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="signup-email-input"
                className="flex-1 px-4 py-2.5 rounded-full border border-border bg-background font-manrope text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              />
              <button
                type="submit"
                disabled={subscribing}
                data-testid="signup-submit-btn"
                className="px-6 py-2.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-60 transition-colors duration-200 whitespace-nowrap"
              >
                {subscribing ? "Joining..." : "Join Free"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ─── Latest Posts ─────────────────────────────────────────────── */}
      <section className="py-16 px-4 sm:px-6 max-w-5xl mx-auto">
        <div className="flex items-baseline justify-between mb-10">
          <div>
            <p className="overline mb-2">From the newsletter</p>
            <h2 className="font-cormorant text-3xl sm:text-4xl font-semibold text-foreground">
              Latest articles
            </h2>
          </div>
          {posts.length > 0 && (
            <Link
              to="/subscribe"
              className="hidden sm:flex items-center gap-1.5 font-manrope text-sm text-brand-accent hover:text-brand-accent-hover transition-colors duration-200"
            >
              View all
              <ArrowRight size={15} />
            </Link>
          )}
        </div>

        {posts.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <p className="font-cormorant text-3xl mb-3 text-foreground">Coming soon</p>
            <p className="font-manrope text-sm">The first issue is being crafted. Subscribe to be the first to know.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <PostCard key={post.id} post={post} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* ─── About / Tip ──────────────────────────────────────────────── */}
      <section className="py-16 px-4 sm:px-6 bg-card border-t border-border">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="overline mb-3">About Elizabeth</p>
            <h2 className="font-cormorant text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              Passionate about writing that moves the needle
            </h2>
            <p className="font-manrope text-sm text-muted-foreground leading-relaxed mb-4">
              I'm Elizabeth Neal — I help businesses, entrepreneurs, and organizations attract
              more customers through clear, engaging content. As a freelance writer and content
              creator, I specialize in blog posts, articles, newsletters, and white papers that
              build authority and strengthen customer relationships.
            </p>
            <p className="font-manrope text-sm text-muted-foreground leading-relaxed mb-6">
              Through FreelanceBlogger.club, I work closely with clients to create content
              tailored to their goals, audience, and brand voice. As a member of AWAI's Circle
              of Success, I'm committed to delivering professional, high-quality writing that
              helps clients stand out in a competitive marketplace.
            </p>
            <Link
              to="/hire"
              data-testid="about-hire-btn"
              className="inline-flex items-center gap-2 font-manrope text-sm font-medium text-brand-accent hover:text-brand-accent-hover transition-colors duration-200"
            >
              See writing packages
              <ArrowRight size={15} />
            </Link>
            <div className="mt-4">
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-linkedin-link"
                className="inline-flex items-center gap-2 font-manrope text-sm font-medium text-muted-foreground hover:text-brand-accent transition-colors duration-200"
              >
                <Linkedin size={15} className="text-brand-accent" />
                Connect with Elizabeth on LinkedIn
              </a>
            </div>
          </div>

          {/* Tip widget */}
          <TipWidget />
        </div>
      </section>

      <LeadCaptureForm />

      <Footer />
    </div>
  );
}

function TipWidget() {
  const [amount, setAmount] = useState(5);
  const [customAmount, setCustomAmount] = useState("");
  const [tipName, setTipName] = useState("");
  const [tipMsg, setTipMsg] = useState("");
  const [tipping, setTipping] = useState(false);

  const amounts = [3, 5, 10, 15];
  const finalAmount = customAmount ? parseFloat(customAmount) : amount;

  const handleTip = async () => {
    if (!finalAmount || finalAmount < 1) { toast.error("Minimum tip is $1"); return; }
    setTipping(true);
    try {
      const res = await axios.post(`${API}/payments/tip-checkout`, {
        amount: finalAmount,
        origin_url: window.location.origin,
        name: tipName || "Anonymous",
        message: tipMsg,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setTipping(false);
    }
  };

  return (
    <div className="bg-background border border-border rounded-2xl p-8" data-testid="tip-widget">
      <div className="flex items-center gap-3 mb-5">
        <Coffee size={24} className="text-brand-accent" />
        <div>
          <h3 className="font-cormorant text-2xl font-semibold text-foreground">Buy me a coffee</h3>
          <p className="font-manrope text-xs text-muted-foreground">Support independent writing</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {amounts.map((a) => (
          <button
            key={a}
            onClick={() => { setAmount(a); setCustomAmount(""); }}
            data-testid={`tip-amount-${a}`}
            className={`px-4 py-2 rounded-full font-manrope text-sm font-medium border transition-colors duration-200 ${
              amount === a && !customAmount
                ? "bg-brand-accent text-white border-brand-accent"
                : "border-border text-foreground hover:bg-muted"
            }`}
          >
            ${a}
          </button>
        ))}
        <input
          type="number"
          placeholder="Custom"
          value={customAmount}
          onChange={(e) => { setCustomAmount(e.target.value); setAmount(0); }}
          data-testid="tip-custom-amount"
          className="w-24 px-3 py-2 rounded-full border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
        />
      </div>

      <input
        type="text"
        placeholder="Your name (optional)"
        value={tipName}
        onChange={(e) => setTipName(e.target.value)}
        data-testid="tip-name-input"
        className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
      />
      <input
        type="text"
        placeholder="Leave a message (optional)"
        value={tipMsg}
        onChange={(e) => setTipMsg(e.target.value)}
        data-testid="tip-message-input"
        className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
      />
      <button
        onClick={handleTip}
        disabled={tipping}
        data-testid="tip-submit-btn"
        className="w-full py-3 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-60 transition-colors duration-200"
      >
        {tipping ? "Processing..." : `Send $${finalAmount || "?"} tip`}
      </button>
    </div>
  );
}
